/**
 * Persists the query string (and path) from the first full document load, on any entry route.
 * Survives SPA navigation, tab close, and browser restart until explicitly cleared.
 * Client-only; no-ops in SSR.
 */

const STORAGE_KEY = "iewc.initialSiteLoadQuery";

export interface InitialSiteLoadQuery {
    search: string;
    queryParams: Record<string, string | string[]>;
    pathname: string;
    href: string;
    capturedAt: number;
}

function isBrowser() {
    return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function parseSearchToRecord(search: string): Record<string, string | string[]> {
    const out: Record<string, string | string[]> = {};
    if (!search || !search.startsWith("?")) {
        return out;
    }
    const params = new URLSearchParams(search);
    for (const key of new Set(params.keys())) {
        const all = params.getAll(key);
        out[key] = all.length <= 1 ? (all[0] ?? "") : all;
    }
    return out;
}

function readStored(): InitialSiteLoadQuery | null {
    if (!isBrowser()) {
        return null;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw) as InitialSiteLoadQuery;
    } catch {
        return null;
    }
}

/**
 * If nothing has been stored yet for this origin, record the current URL (first paint / landing).
 * Call once on app shell mount (e.g. PageLayout). Safe to call multiple times: only the first
 * run across the lifetime of the device writes (until cleared).
 */
export function captureInitialSiteLoadQueryIfNeeded() {
    if (!isBrowser()) {
        return;
    }
    if (localStorage.getItem(STORAGE_KEY) !== null) {
        return;
    }

    const { pathname, search, href } = window.location;
    const payload: InitialSiteLoadQuery = {
        search,
        queryParams: parseSearchToRecord(search),
        pathname,
        href,
        capturedAt: Date.now(),
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // Quota or private mode: ignore; callers can use get and see null
    }
}

/**
 * Data captured on the first full load, or null if not captured / unreadable.
 */
export function getInitialSiteLoadQuery(): InitialSiteLoadQuery | null {
    return readStored();
}

/**
 * Removes stored landing data so the next full page load can be captured again.
 */
export function clearInitialSiteLoadQuery() {
    if (!isBrowser()) {
        return;
    }
    localStorage.removeItem(STORAGE_KEY);
}

/**
 * @internal for tests and introspection
 */
export const initialSiteLoadQueryStorageKey = STORAGE_KEY;
